import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';
import { icon } from '../shared/components/icons';
import { infoIcon } from '../shared/components/InfoIcon';
import { paginate, pager } from '../shared/lib/tableControls';

// Guard a numeric value → "—" when missing / non-finite.
const num = (v: number | null | undefined): string =>
  v != null && Number.isFinite(Number(v)) ? Number(v).toLocaleString() : '—';
const pct = (v: number | null | undefined): string =>
  v != null && Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v).toFixed(1) + '%' : '—';

interface ReviewKPIs {
  total_reviews: number;
  avg_rating: number;
  avg_rating_90?: number | null;
  avg_rating_prev_90?: number | null;
  pct_with_text: number;
  pct_responded: number;
  published_count: number;
}

interface ReviewRow {
  hostaway_review_id: number;
  listing_name: string;
  reviewer_name: string;
  rating: number | null;
  public_review: string | null;
  private_feedback: string | null;
  status: string;
  submitted_at: string | null;
  departure_date: string | null;
  is_recommended: boolean | null;
  reviewee_response: string | null;
  marked_responded?: boolean;
  challenge?: { status: string; at?: string; by?: string | null; note?: string } | null;   // R104 #4
}

interface ReviewsApiResponse {
  kpis: ReviewKPIs;
  rows: ReviewRow[];
}

export class ReviewsPage {
  private container: HTMLElement;
  private kpis: ReviewKPIs | null = null;
  // R80: per-property score list (sortable, 30/60/90/120 trend, range filter).
  private byProp: any = null;            // /api/reviews/by-property payload; {error:true} on failure
  private bpSort: string = 'name_asc';   // name_asc | name_desc | avg_desc | avg_asc | count_desc
  private bpFrom = '';
  private bpTo = '';
  private bpSeq = 0;   // review #12: a slow earlier fetch must not overwrite a newer filter's result
  private issuesSeq = 0;      // AI-unblock: only the newest /api/reviews/issues read paints
  private analyzing = 0;      // AI-unblock: background analyses running (note shown above the list while > 0)
  private allRows: ReviewRow[] = [];

  // Filter state
  private filterListingId: string = '';
  private filterStatus: string = 'all';
  private filterRating: string = 'all';
  private filterResponse: string = 'all';
  private filterChallenge = false;       // R104 #4: only reviews with a challenge status
  private search: string = '';

  // Pagination (client-side, over the filtered list)
  private page: number = 1;
  private readonly pageSize: number = 25;

  // Expanded review IDs
  private expandedIds: Set<number> = new Set();



  constructor(container: HTMLElement) {
    this.container = container;
  }

  async render(): Promise<void> {
    this.container.innerHTML = `
      <div class="page-content">

        <div class="dash-head">
          <h1>Guest Reviews</h1>
          <p>Every guest review across the portfolio — watch Average Rating and clear the Needs Response queue first.</p>
        </div>

        <style>
          #rv-filter-bar select:focus, #rv-filter-bar input:focus { outline:none; border-color:var(--accent); box-shadow:var(--focus-ring); }

          /* ── Cards / panels: white surface, soft elevation, generous spacing ── */
          .page-content .pf-section {
            background:var(--card);
            border:1px solid var(--border);
            border-radius:var(--radius);
            box-shadow:var(--shadow-card);
            overflow:hidden;
          }
          .page-content .pf-section-hdr {
            padding:16px 18px;
            border-bottom:1px solid var(--border-soft);
          }
          .page-content .pf-section-hdr h3 {
            color:var(--text);
            font-weight:600;
            letter-spacing:-0.01em;
          }
          .page-content .pf-section-hdr p {
            color:var(--text-muted);
          }

          /* ── Filter bar inputs: crisp, rounded, subtle hover ── */
          #rv-filter-bar .rv-filter-select { transition:border-color 0.12s, box-shadow 0.12s, background 0.12s; }
          #rv-filter-bar .rv-filter-select:hover { border-color:var(--border-strong, var(--accent)); }

          /* Status tab group: clean pill segments with a lifted active chip */
          .rv-tab-group { border:1px solid var(--border); }
          .rv-tab {
            border:none;
            background:transparent;
            color:var(--text-muted);
            font-size:12px;
            font-weight:600;
            padding:5px 12px;
            border-radius:calc(var(--radius) - 6px);
            cursor:pointer;
            transition:background 0.12s, color 0.12s, box-shadow 0.12s;
          }
          .rv-tab:hover { color:var(--text); background:var(--bg-subtle); }
          .rv-tab-active, .rv-tab.rv-tab-active {
            background:var(--card);
            color:var(--text);
            box-shadow:var(--shadow-card);
          }

          /* Rating chips: pill badges, restrained, with an active dark fill */
          .rv-rating-chip {
            border-radius:var(--radius-pill);
            font-size:11px;
            font-weight:600;
            padding:4px 11px;
            background:var(--bg-subtle);
            color:var(--text-muted);
            font-variant-numeric:tabular-nums;
            transition:background 0.12s, color 0.12s;
          }
          .rv-rating-chip:hover { background:var(--border-soft); color:var(--text); }
          .rv-rating-chip.rv-chip-active {
            background:var(--text);
            color:#fff;
          }

          /* ── Review list rows: roomy, soft dividers, gentle hover lift ── */
          .rv-review-item {
            border-bottom:1px solid var(--border-soft) !important;
            transition:background 0.12s;
          }
          .rv-review-item:last-child { border-bottom:none !important; }
          .rv-review-item:hover { background:var(--bg-subtle); }
          .rv-review-item.rv-expanded { background:var(--bg-subtle); }

          /* Reviewer avatar: soft circular token */
          .rv-avatar {
            width:36px;
            height:36px;
            border-radius:var(--radius-pill);
            background:var(--bg-subtle);
            color:var(--text-muted);
            border:1px solid var(--border);
            display:flex;
            align-items:center;
            justify-content:center;
            font-size:12px;
            font-weight:600;
            flex-shrink:0;
          }
          .rv-stars { letter-spacing:1px; line-height:1; }
          .rv-expand-btn:hover { color:var(--text-muted) !important; }

          /* Pill badges already in markup → tabular figures + clean radius */
          .page-content .pf-chip { border-radius:var(--radius-pill); font-variant-numeric:tabular-nums; }

          @media (max-width:640px) {
            #rv-filter-bar { width:100%; }
          }
        </style>

        <!-- KPI Grid -->
        <div id="rv-kpi-grid" class="kpi-grid">
          <div style="grid-column:1/-1; display:flex; justify-content:center; padding:32px;">${LoadingSpinner.render()}</div>
        </div>

        <!-- The work: low-rated reviews awaiting a reply (queue first) -->
        <div id="rv-queue"></div>

        <!-- AI issue analysis: what are the reviews telling us to fix? -->
        <div class="pf-section" style="margin-bottom:20px;">
          <div class="pf-section-hdr">
            <div>
              <h3>Issues arising</h3>
              <p>Claude clusters the last 90 days of sub-8 reviews into operational themes; high-severity themes open tickets automatically</p>
            </div>
            <button class="btn btn-primary btn-sm" id="rv-analyze" style="margin-left:auto;">🧠 Analyze reviews</button>
          </div>
          <div id="rv-issues" style="padding:14px 18px;">
            <div style="font-size:12px; color:var(--text-muted);">Loading the latest analysis…</div>
          </div>
        </div>

        <!-- R80: per-property scores -->
        <div class="pf-section" style="margin-bottom:20px;">
          <div class="pf-section-hdr">
            <div>
              <h3>Scores by property</h3>
              <p>Every property's review average and trend — 30 / 60 / 90 / 120-day windows trail from today; the range column follows the date filter.</p>
            </div>
            <div id="rv-bp-bar" style="display:flex; align-items:center; gap:10px; flex-wrap:wrap;">
              <span style="font-size:11.5px; color:var(--text-muted);">click a column header to sort · click a property for all its reviews</span>
              <label style="font-size:11.5px; color:var(--text-muted); display:flex; gap:4px; align-items:center;">From <input type="date" id="rv-bp-from" style="padding:5px 8px; border:1px solid var(--border); border-radius:8px; font-size:12px;"></label>
              <label style="font-size:11.5px; color:var(--text-muted); display:flex; gap:4px; align-items:center;">To <input type="date" id="rv-bp-to" style="padding:5px 8px; border:1px solid var(--border); border-radius:8px; font-size:12px;"></label>
            </div>
          </div>
          <div id="rv-bp-body"><div style="padding:30px; text-align:center;">${LoadingSpinner.render()}</div></div>
        </div>

        <!-- Filter bar -->
        <div class="pf-section" style="margin-bottom:20px;">
          <div class="pf-section-hdr">
            <div>
              <h3>Guest Reviews</h3>
              <p>Sorted by submission date · click a review to expand</p>
            </div>
            <div id="rv-filter-bar" style="display:flex; align-items:center; gap:12px; flex-wrap:wrap;">
              ${LoadingSpinner.render()}
            </div>
          </div>
          <div id="rv-list-container">
            <div style="padding:40px; text-align:center; color:var(--text-muted);">${LoadingSpinner.render()}</div>
          </div>
        </div>

      </div>
    `;

    document.getElementById('rv-analyze')?.addEventListener('click', () => this.loadIssues(true));
    document.getElementById('rv-bp-from')?.addEventListener('change', (e) => { this.bpFrom = (e.target as HTMLInputElement).value; void this.loadByProp(); });
    document.getElementById('rv-bp-to')?.addEventListener('change', (e) => { this.bpTo = (e.target as HTMLInputElement).value; void this.loadByProp(); });
    this.loadIssues();
    void this.loadByProp();
    await this.loadData();
  }

  // AI-unblock (Jesse: the AI "cannot prevent people from doing their jobs"):
  // paint the stored analysis + open tickets FIRST, then run the (possibly slow,
  // possibly out-of-credits) Claude analysis behind them and repaint when it
  // lands. The tickets are workable the whole time.
  private async loadIssues(runAnalysis = false): Promise<void> {
    if (!document.getElementById('rv-issues')) return;
    this.analyzing += 1;
    await this.refreshIssues();
    let refreshed = runAnalysis;
    let failed = false;
    let unsaved: any = null;   // R114 fix: an analysis that ran but couldn't be stored — paint it from the response
    try {
      const r = await fetch('/api/reviews/analyze', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(runAnalysis ? { force: true } : {}) });
      const j = r.ok ? await r.json().catch(() => null) : null;
      if (!r.ok) failed = true;
      // R114 fix2: only a REAL analysis (Claude / no sub-8 reviews) is painted
      // unsaved. A template grouping never replaces the last good AI analysis
      // — not in the store and not on the page: treat it like a failed re-run
      // and repaint what is stored.
      else if (j && j.saved === false && j.source !== 'template' && Array.isArray(j.issues)) unsaved = j;
      else if (j && j.saved === false) failed = true;
      else if (j && j.refreshed) refreshed = true;
    } catch (_) { failed = true; }
    this.analyzing -= 1;
    if (unsaved) {
      // the server holds a paid analysis it couldn't store and retries the save on the next open (no new AI run)
      await this.refreshIssues("This analysis ran but couldn't be saved just now — showing it unsaved. Reopening the page retries the save without re-running the AI.", false, unsaved);
      return;
    }
    // nothing new → just drop the "analyzing" note; otherwise repaint from the store
    await this.refreshIssues(failed && runAnalysis ? "Couldn't re-run the analysis just now — showing the last one." : '', !refreshed && !failed);
  }

  // GET-only repaint (ticket / theme clicks use this — no analysis re-run).
  // quiet = only clear the "analyzing" note, keep the painted list as-is.
  private async refreshIssues(note = '', quiet = false, analysisOverride: any = null): Promise<void> {
    const el = document.getElementById('rv-issues');
    if (!el) return;
    if (quiet && el.querySelector('[data-rv-issues-painted]')) {
      if (this.analyzing > 0) return;   // another run is still going — its note stays
      el.querySelector('[data-rv-analyzing]')?.remove();
      const empty = el.querySelector('[data-rv-analyzing-empty]');
      if (empty) empty.textContent = 'No analysis yet — hit Analyze reviews.';
      return;
    }
    const seq = ++this.issuesSeq;
    try {
      const r = await fetch('/api/reviews/issues');
      const got = r.ok ? await r.json() : { analysis: null, tickets: [], theme_status: {} };
      const { tickets, theme_status } = got;
      const analysis = analysisOverride || got.analysis;
      if (seq !== this.issuesSeq) return;   // a newer read is on its way
      const themeStatus: Record<string, { status: string; at?: string }> = theme_status || {};
      const statusOf = (theme: string) => themeStatus[String(theme || '').trim().toLowerCase()];
      const SEV_PILL: Record<string, string> = { high: 'pill-red', medium: 'pill-amber', low: 'pill-gray' };
      const sevChip = (s: string) => `<span class="pill ${SEV_PILL[s] || SEV_PILL.low}">${Formatters.escapeHtml(s || 'low')}</span>`;

      // Themes the team has marked reviewed/dismissed collapse out of the main
      // list (feedback: "there should be a way to dismiss these or they will
      // stay there forever") — they land in "Handled themes" below, with Undo.
      const allIssues: any[] = analysis?.issues || [];
      const activeIssues = allIssues.filter(i => !statusOf(i.theme));
      const handledIssues = allIssues.filter(i => !!statusOf(i.theme));

      const issues = activeIssues.map((i: any) => `
        <div style="border:1px solid var(--border); border-radius:8px; padding:10px 14px; margin-bottom:8px;">
          <div style="display:flex; align-items:baseline; gap:8px; flex-wrap:wrap;">
            <span style="font-size:13px; font-weight:600;">${Formatters.escapeHtml(i.theme)}</span>
            ${sevChip(i.severity)}
            <span style="font-size:11px;">${(i.properties || []).slice(0, 4).map((p: string) => `<a class="rv-issue-prop" data-prop="${Formatters.escapeHtml(p)}" style="color:var(--info); cursor:pointer; margin-right:6px;">${Formatters.escapeHtml(p)}</a>`).join('')}</span>
            <span style="margin-left:auto; display:flex; gap:6px;">
              <button class="btn btn-secondary btn-sm" data-rv-theme="${Formatters.escapeHtml(i.theme)}" data-rv-theme-status="reviewed" style="font-size:10px; padding:2px 8px;" title="Seen and handled — moves to Handled themes below">Mark reviewed</button>
              <button class="btn btn-secondary btn-sm" data-rv-theme="${Formatters.escapeHtml(i.theme)}" data-rv-theme-status="dismissed" style="font-size:10px; padding:2px 8px;" title="Not actionable — moves to Handled themes below">Dismiss</button>
            </span>
          </div>
          ${(i.evidence || []).slice(0, 2).map((e: string) => `<div style="font-size:11px; color:var(--text-faint, #9ca3af); margin-top:4px;">${Formatters.escapeHtml(e)}</div>`).join('')}
          ${i.suggested_action ? `<div style="font-size:12px; margin-top:6px;"><span style="color:var(--text-muted);">Fix:</span> ${Formatters.escapeHtml(i.suggested_action)}</div>` : ''}
        </div>`).join('');

      // Tickets are one-listing-each server-side (analyze splits legacy grouped
      // ones), so the open list reads as a per-property worklist: a sub-header
      // per property (deep-linking to its Why tab), ticket rows underneath.
      const openTickets = (tickets || []).filter((t: any) => ['open', 'in_progress'].includes(t.status));
      const groups = new Map<string, { name: string; lid: string; items: any[] }>();
      for (const t of openTickets) {
        // Defensive: a not-yet-split grouped ticket files under its first name,
        // without a deep link (a wrong link is worse than none).
        const name = (t.properties || [])[0] || String((t.listing_ids || [])[0] || 'Unknown property');
        const lid = (t.listing_ids || []).length === 1 ? String(t.listing_ids[0]) : '';
        const key = lid || name;
        if (!groups.has(key)) groups.set(key, { name, lid, items: [] });
        groups.get(key)!.items.push(t);
      }
      const sevRank: Record<string, number> = { high: 0, medium: 1, low: 2 };
      const ageLabel = (t: any): string => {
        const opened = t.created_at || t.asof;
        const days = opened ? Math.floor((Date.now() - new Date(opened).getTime()) / 86400000) : NaN;
        return Number.isFinite(days) && days >= 0 ? (days === 0 ? '<1d' : `${days}d`) : '';
      };
      const ticketGroups = Array.from(groups.values())
        .sort((a, b) => b.items.length - a.items.length || a.name.localeCompare(b.name))
        .map(g => {
          const header = g.lid
            ? `<a class="rv-ticket-prop" data-lid="${Formatters.escapeHtml(g.lid)}" style="font-size:12px; font-weight:600; color:var(--info); cursor:pointer;" title="Open the Why tab for ${Formatters.escapeHtml(g.name)}">${Formatters.escapeHtml(g.name)}</a>`
            : `<span style="font-size:12px; font-weight:600;">${Formatters.escapeHtml(g.name)}</span>`;
          const rows = g.items
            .sort((a: any, b: any) => (sevRank[a.severity] ?? 3) - (sevRank[b.severity] ?? 3) || String(a.created_at || '').localeCompare(String(b.created_at || '')))
            .map((t: any) => {
              const idAttr = Formatters.escapeHtml(String(t.id));
              const opened = String(t.created_at || t.asof || '').slice(0, 10);
              return `
            <div style="display:flex; align-items:center; gap:10px; padding:6px 0 6px 14px; border-bottom:1px solid var(--border-soft); font-size:12px; flex-wrap:wrap;">
              <span style="flex:1; min-width:0;">${Formatters.escapeHtml(t.theme)}</span>
              ${sevChip(t.severity)}
              ${t.status === 'in_progress' ? `<span class="pill pill-amber">in progress</span>` : ''}
              <span class="mono num" style="font-size:11px; color:var(--text-muted);" title="open since ${Formatters.escapeHtml(opened)}">${ageLabel(t)}</span>
              ${t.status === 'open' ? `<button class="btn btn-secondary btn-sm" data-rv-ticket="${idAttr}" data-rv-status="in_progress" style="font-size:10px; padding:2px 8px;">Start</button>` : ''}
              <button class="btn btn-secondary btn-sm" data-rv-ticket="${idAttr}" data-rv-status="done" style="font-size:10px; padding:2px 8px;">Done</button>
              <button class="btn btn-secondary btn-sm" data-rv-ticket="${idAttr}" data-rv-status="dismissed" style="font-size:10px; padding:2px 8px;">Dismiss</button>
            </div>`;
            }).join('');
          return `
          <div style="margin-top:10px;">
            <div style="display:flex; align-items:baseline; gap:8px;">
              ${header}
              <span style="font-size:11px; color:var(--text-muted);">${g.items.length} open ticket${g.items.length === 1 ? '' : 's'}</span>
            </div>
            ${rows}
          </div>`;
        }).join('');

      const handled = handledIssues.length ? `
        <div style="margin-top:16px;">
          <div style="font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; margin-bottom:4px;">Handled themes · ${handledIssues.length} of ${allIssues.length} in this analysis</div>
          ${handledIssues.map((i: any) => {
            const st = statusOf(i.theme);
            const when = st?.at ? ` ${Formatters.formatDate(st.at)}` : '';
            return `
            <div style="display:flex; align-items:center; gap:10px; padding:5px 0; border-bottom:1px solid var(--border-soft); font-size:12px; color:var(--text-muted);">
              <span style="flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${Formatters.escapeHtml(i.theme)}</span>
              <span class="pill pill-gray">${st?.status === 'dismissed' ? 'dismissed' : 'reviewed'}${when}</span>
              <button class="btn btn-secondary btn-sm" data-rv-theme="${Formatters.escapeHtml(i.theme)}" data-rv-theme-status="open" style="font-size:10px; padding:2px 8px;">Undo</button>
            </div>`;
          }).join('')}
        </div>` : '';

      // AI-unblock: say plainly when the AI part didn't happen — the list is
      // still the team's worklist; the server retries Claude within the hour.
      const aiNote = !analysis ? ''
        : analysis.ai_retry ? ` · <span style="color:var(--warning, #b45309);">AI refresh didn't run (${Formatters.escapeHtml(String(analysis.ai_retry.kind || 'error').replace(/_/g, ' '))}) — showing the last AI read; it retries automatically</span>`
        : analysis.source === 'template' ? ' · <span style="color:var(--warning, #b45309);">AI unavailable — basic grouping by property; it retries automatically</span>'
        : '';
      // AI-unblock (review #21): an error note (e.g. a ticket save that failed)
      // shows even while a background analysis is running — clicks happen then.
      const busy = (this.analyzing > 0
        ? '<div data-rv-analyzing style="font-size:11.5px; color:var(--text-muted); margin-bottom:8px;">Reading the last 90 days of reviews in the background — everything below is usable meanwhile…</div>'
        : '') + (note ? `<div style="font-size:11.5px; color:var(--danger); margin-bottom:8px;">${Formatters.escapeHtml(note)}</div>` : '');
      const ticketsOpen = !!el.querySelector('details[open]');   // a background repaint must not collapse the worklist someone has open
      el.innerHTML = `
        <span data-rv-issues-painted hidden></span>
        ${busy}
        ${analysis ? `<div style="font-size:11px; color:var(--text-faint, #9ca3af); margin-bottom:10px;">As of ${Formatters.escapeHtml(String(analysis.asof || ''))} · ${analysis.reviews_analyzed} sub-8 reviews read · source: ${Formatters.escapeHtml(String(analysis.source || ''))}${aiNote}</div>` : ''}
        ${issues || (allIssues.length
          ? '<div style="font-size:12px; color:var(--text-muted);">All themes in this analysis are handled (see below) — re-run Analyze reviews for a fresh read.</div>'
          : `<div style="font-size:12px; color:var(--text-muted);">${this.analyzing > 0 ? '<span data-rv-analyzing-empty>No analysis stored yet — the first one is running…</span>' : 'No analysis yet — hit Analyze reviews.'}</div>`)}
        ${openTickets.length ? `<details style="margin:14px 0 0;"${ticketsOpen ? ' open' : ''}><summary style="font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em; cursor:pointer; list-style:revert;">Open tickets · ${openTickets.length} across ${groups.size} propert${groups.size === 1 ? 'y' : 'ies'} · one ticket = one listing</summary><div style="margin-top:8px;">${ticketGroups}</div></details>` : ''}
        ${handled}`;
      el.querySelectorAll<HTMLElement>('.rv-issue-prop').forEach(a => a.addEventListener('click', () => {
        this.filterListingId = a.dataset.prop || '';
        const sel = document.getElementById('rv-listing-select') as HTMLSelectElement | null;
        if (sel) sel.value = this.filterListingId;
        this.page = 1;
        this.renderList();
        document.getElementById('rv-list-container')?.scrollIntoView({ behavior: 'smooth' });
      }));
      // Ticket property chip → the Property Window's Why tab (TriagePage pattern).
      el.querySelectorAll<HTMLElement>('.rv-ticket-prop').forEach(a => a.addEventListener('click', () => {
        sessionStorage.setItem('workup_preselect', a.dataset.lid || '');
        sessionStorage.setItem('workup_tab', 'why');
        document.dispatchEvent(new CustomEvent('app:navigate', { detail: 'data-explorer' }));
      }));
      el.querySelectorAll<HTMLElement>('[data-rv-ticket]').forEach(btn => btn.addEventListener('click', async () => {
        const r = await fetch('/api/reviews/issues', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: btn.dataset.rvTicket, status: btn.dataset.rvStatus }) }).catch(() => null);
        // AI-unblock: a ticket click never waits on (or re-runs) the analysis.
        // AI-unblock (review #21): the server now refuses (instead of wiping the
        // board) when the ticket store is unreadable — say so rather than
        // silently repainting the old status.
        this.refreshIssues(r && r.ok ? '' : "Couldn't save that ticket change — try again.");
      }));
      // Theme-level reviewed/dismissed/open (Undo) — server keys the map by
      // lowercased theme, so the raw theme string is fine to send as-is.
      el.querySelectorAll<HTMLElement>('[data-rv-theme]').forEach(btn => btn.addEventListener('click', async () => {
        await fetch('/api/reviews/theme-status', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ theme: btn.dataset.rvTheme, status: btn.dataset.rvThemeStatus }) });
        this.refreshIssues();
      }));
    } catch (_) {
      if (seq !== this.issuesSeq) return;
      el.innerHTML = '<div style="font-size:12px; color:var(--danger);">Couldn’t load the issues — try again.</div>';
    }
  }

  // R80: per-property score list.
  private async loadByProp(): Promise<void> {
    const seq = ++this.bpSeq;
    const q = new URLSearchParams();
    if (this.bpFrom) q.set('from', this.bpFrom);
    if (this.bpTo) q.set('to', this.bpTo);
    let next: any;
    try {
      const r = await fetch(`/api/reviews/by-property${q.toString() ? `?${q}` : ''}`);
      next = r.ok ? await r.json() : { error: true };
    } catch { next = { error: true }; }
    if (seq !== this.bpSeq) return;   // superseded by a newer filter change
    this.byProp = next;
    this.renderByProp();
  }
  private renderByProp(): void {
    const host = document.getElementById('rv-bp-body');
    if (!host) return;
    const j = this.byProp;
    if (!j) { host.innerHTML = `<div style="padding:30px; text-align:center;">${LoadingSpinner.render()}</div>`; return; }
    if (j.error) { host.innerHTML = '<div style="padding:20px; color:var(--danger-text,#b91c1c);">⚠ Couldn\'t load property scores — check the server log.</div>'; return; }
    const esc = (x: any) => String(x == null ? '' : x).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const rows: any[] = [...(j.rows || [])];
    const avgOf = (r: any) => (this.bpFrom || this.bpTo) ? r.range.avg : r.total.avg;
    // R104 #4: every column sorts high↔low. bpSort = `${col}_${asc|desc}`; nulls sink either way.
    const trendOf = (r: any) => (r.w30.avg == null || r.w120.avg == null) ? null : Math.round((r.w30.avg - r.w120.avg) * 100) / 100;
    const valOf: Record<string, (r: any) => any> = {
      name: r => r.name, avg: avgOf, w30: r => r.w30.avg, w60: r => r.w60.avg, w90: r => r.w90.avg, w120: r => r.w120.avg, trend: trendOf,
      count: r => (this.bpFrom || this.bpTo) ? r.range.count : r.total.count,
    };
    const [col, dir] = (() => { const ix = this.bpSort.lastIndexOf('_'); return ix > 0 ? [this.bpSort.slice(0, ix), this.bpSort.slice(ix + 1)] : ['name', 'asc']; })();
    const get = valOf[col] || valOf.name;
    rows.sort((a, b) => {
      const va = get(a), vb = get(b);
      if (va == null && vb == null) return a.name.localeCompare(b.name);
      if (va == null) return 1; if (vb == null) return -1;
      const c = typeof va === 'string' ? va.localeCompare(vb) : (va - vb);
      return (dir === 'desc' ? -c : c) || a.name.localeCompare(b.name);
    });
    const th = (key: string, label: string, align: 'left' | 'right', title = '') => {
      const on = col === key; const arrow = on ? (dir === 'desc' ? ' ▼' : ' ▲') : '';
      return `<th data-rv-bpcol="${key}" title="${esc(title || `Sort by ${label}`)}" style="text-align:${align}; padding:9px ${align === 'left' ? '14px' : '10px'}; font-size:10.5px; text-transform:uppercase; letter-spacing:0.05em; color:${on ? 'var(--text)' : 'var(--text-muted)'}; cursor:pointer; user-select:none; white-space:nowrap;">${esc(label)}${arrow}</th>`;
    };
    const tone = (v: number | null) => v == null ? '' : v >= 4.8 ? 'color:var(--success-text,#166534); font-weight:700;' : v < 4.5 ? 'color:var(--danger-text,#b91c1c); font-weight:700;' : '';
    const cell = (w: any) => w.rated
      ? `<span class="num mono" style="${tone(w.avg)}">${w.avg?.toFixed(2)}</span> <span style="color:var(--text-faint,#9ca3af); font-size:10.5px;">(${w.rated})</span>`
      : '<span style="color:var(--text-faint,#9ca3af);">—</span>';
    const trendArrow = (r: any) => {
      if (r.w30.avg == null || r.w120.avg == null) return '';
      const d = Math.round((r.w30.avg - r.w120.avg) * 100) / 100;
      if (Math.abs(d) < 0.05) return '<span style="color:var(--text-faint,#9ca3af);">→</span>';
      return d > 0 ? `<span style="color:var(--success-text,#166534);" title="30d avg vs 120d avg">▲ +${d.toFixed(2)}</span>` : `<span style="color:var(--danger-text,#b91c1c);" title="30d avg vs 120d avg">▼ ${d.toFixed(2)}</span>`;
    };
    const rangeCol = (this.bpFrom || this.bpTo);
    host.innerHTML = `
      <div style="overflow-x:auto;">
        <table style="width:100%; border-collapse:collapse; font-size:12.5px; font-variant-numeric:tabular-nums;">
          <thead><tr style="border-bottom:1px solid var(--border);">
            ${th('name', 'Property', 'left')}
            ${th('avg', rangeCol ? 'Avg (range)' : 'Avg (all-time)', 'right')}
            ${th('count', 'Reviews', 'right', 'Number of reviews')}
            ${th('w30', '30d', 'right')}
            ${th('w60', '60d', 'right')}
            ${th('w90', '90d', 'right')}
            ${th('w120', '120d', 'right')}
            ${th('trend', 'Trend', 'right', '30-day average vs 120-day average')}
          </tr></thead>
          <tbody>
            ${rows.map(r => `<tr style="border-bottom:1px solid var(--border-soft,#f1f5f9);">
              <td style="padding:8px 14px; font-weight:600;"><button data-rv-bpprop="${esc(r.name)}" title="Show every review for this property" style="background:none; border:none; padding:0; font:inherit; font-weight:600; color:var(--accent,#2563eb); cursor:pointer; text-align:left;">${esc(r.name)}</button>${r.active ? '' : ' <span style="font-size:10px; color:var(--text-faint,#9ca3af);">(inactive)</span>'}</td>
              <td style="text-align:right; padding:8px 10px;">${cell(rangeCol ? r.range : r.total)}</td>
              <td style="text-align:right; padding:8px 10px;"><span class="num mono">${(rangeCol ? r.range : r.total).count ?? 0}</span></td>
              <td style="text-align:right; padding:8px 10px;">${cell(r.w30)}</td>
              <td style="text-align:right; padding:8px 10px;">${cell(r.w60)}</td>
              <td style="text-align:right; padding:8px 10px;">${cell(r.w90)}</td>
              <td style="text-align:right; padding:8px 10px;">${cell(r.w120)}</td>
              <td style="text-align:right; padding:8px 14px;">${trendArrow(r)}</td>
            </tr>`).join('')}
          </tbody>
        </table>
      </div>
      <div style="padding:8px 14px 12px; font-size:11px; color:var(--text-muted);">${esc(j.basis || '')}</div>`;
    // R104 #4: header click = sort (same column flips the direction); property click = that property's reviews.
    host.querySelectorAll<HTMLElement>('[data-rv-bpcol]').forEach(h => h.addEventListener('click', () => {
      const key = h.dataset.rvBpcol!;
      const cur = this.bpSort;
      const curCol = cur.slice(0, cur.lastIndexOf('_')), curDir = cur.slice(cur.lastIndexOf('_') + 1);
      const nextDir = curCol === key ? (curDir === 'desc' ? 'asc' : 'desc') : (key === 'name' ? 'asc' : 'desc');
      this.bpSort = `${key}_${nextDir}`;
      this.renderByProp();
    }));
    host.querySelectorAll<HTMLElement>('[data-rv-bpprop]').forEach(b => b.addEventListener('click', () => this.showPropertyReviews(b.dataset.rvBpprop!)));
  }

  private async loadData(): Promise<void> {
    try {
      const res = await fetch('/api/reviews');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: ReviewsApiResponse = await res.json();
      this.kpis = data.kpis;
      this.allRows = data.rows;

      // R102 O2: the issues board parked review ids — clear the filters, expand
      // those reviews, land on their page and scroll the first one into view.
      let focus: { ids: number[]; listing_name: string } | null = null;
      try {
        const raw = sessionStorage.getItem('rv_focus_review');
        if (raw) { sessionStorage.removeItem('rv_focus_review'); focus = JSON.parse(raw); }
      } catch { focus = null; }
      let scrollTo: number | null = null;
      if (focus) {
        this.search = ''; this.filterStatus = 'all'; this.filterRating = 'all'; this.filterResponse = 'all';
        const ids = (focus.ids || []).map(Number).filter(n => this.allRows.some(r => r.hostaway_review_id === n));
        this.filterListingId = ids.length ? '' : (focus.listing_name && this.allRows.some(r => r.listing_name === focus!.listing_name) ? focus.listing_name : '');
        for (const id of ids) this.expandedIds.add(id);
        const rows = this.filteredRows();
        const ix = ids.length ? rows.findIndex(r => r.hostaway_review_id === ids[0]) : -1;
        this.page = ix >= 0 ? Math.floor(ix / this.pageSize) + 1 : 1;
        scrollTo = ids.length ? ids[0] : null;
      }

      this.renderKpis();
      this.renderQueue();
      this.renderFilterBar();
      this.renderList();
      if (scrollTo != null) {
        const el = document.querySelector(`.rv-review-item[data-review-id="${scrollTo}"]`) as HTMLElement | null;
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.style.outline = '2px solid var(--accent)'; el.style.outlineOffset = '2px'; setTimeout(() => { el.style.outline = ''; el.style.outlineOffset = ''; }, 4000); }
      }
    } catch (err) {
      console.error('Reviews load failed:', err);
      const kpiEl = document.getElementById('rv-kpi-grid');
      if (kpiEl) kpiEl.innerHTML = `<div style="grid-column:1/-1;">${emptyState('Could not load reviews', 'We hit a problem fetching review data. Refresh the page, and if it keeps failing the sync may still be running.', '⚠️')}</div>`;
      const listEl = document.getElementById('rv-list-container');
      if (listEl) listEl.innerHTML = '';
      const filterEl = document.getElementById('rv-filter-bar');
      if (filterEl) filterEl.innerHTML = '';
    }
  }

  private renderKpis(): void {
    if (!this.kpis) return;
    const el = document.getElementById('rv-kpi-grid');
    if (!el) return;
    const k = this.kpis;
    const nr = this.needsResponseCount();
    const days30 = Date.now() - 30 * 86400000;
    const last30 = this.allRows.filter(r => r.submitted_at && new Date(r.submitted_at).getTime() >= days30).length;
    const challenged = this.allRows.filter(r => r.challenge && r.challenge.status).length;   // R104 #4
    const wins = this.allRows.filter(r => r.challenge && r.challenge.status === 'successful').length;
    const delta = (k.avg_rating_90 != null && k.avg_rating_prev_90 != null)
      ? Math.round((k.avg_rating_90 - k.avg_rating_prev_90) * 100) / 100 : null;
    const card = (label: string, icoName: string, value: string, cap: string, tone = '') => `
      <div class="kpi-card ${tone}">
        <div class="kpi-card-top"><span class="kpi-card-label">${label}</span><span class="kpi-card-ic">${icon(icoName, 17)}</span></div>
        <div class="kpi-card-value">${value}</div>
        <div class="kpi-card-cap">${cap}</div>
      </div>`;
    const avgVal = Number.isFinite(Number(k.avg_rating)) && k.avg_rating > 0 ? k.avg_rating.toFixed(2) : '—';
    const avgTone = Number.isFinite(Number(k.avg_rating)) && k.avg_rating >= 4.5 ? 'tone-good' : '';
    el.innerHTML = `
      ${card(`Avg rating ${infoIcon('avg_rating')}`, 'star', avgVal,
        delta != null ? `last 90d ${k.avg_rating_90?.toFixed(2)} · ${delta >= 0 ? '+' : ''}${delta.toFixed(2)} vs prior 90d` : 'out of 5.0',
        avgTone)}
      ${card('Needs response', 'flag', String(nr), 'low-rated, no reply yet', nr > 0 ? 'tone-bad' : '')}
      ${card(`Response rate ${infoIcon('response_rate')}`, 'pulse', pct(k.pct_responded), 'reviews with a host response')}
      ${card('Reviews · 30d', 'calendar', String(last30), `${num(k.published_count)} published all-time${challenged ? ` · ⚑ ${challenged} challenged${wins ? `, ${wins} won` : ''}` : ''}`)}`;
  }

  // Block B — the needs-response queue, promoted out of the filter bar.
  private renderQueue(): void {
    const el = document.getElementById('rv-queue');
    if (!el) return;
    const items = this.allRows.filter(r => this.isNeedsResponse(r))
      .sort((a, b) => String(a.submitted_at || '').localeCompare(String(b.submitted_at || '')));
    if (!items.length) { el.innerHTML = ''; return; }
    const rows = items.slice(0, 10).map(r => {
      const days = r.submitted_at ? Math.floor((Date.now() - new Date(r.submitted_at).getTime()) / 86400000) : null;
      return `<div style="display:flex; align-items:center; gap:12px; padding:8px 0; border-bottom:1px solid var(--border-soft); font-size:12px;">
        <span style="font-weight:600; min-width:140px; max-width:200px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">${Formatters.escapeHtml(r.listing_name || '')}</span>
        <span class="mono num" style="color:var(--danger);">${r.rating != null ? r.rating.toFixed(1) + '★' : 'no rating'}</span>
        <span style="flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; color:var(--text-muted);">${Formatters.escapeHtml((r.public_review || '').slice(0, 100))}</span>
        <span class="mono num" style="${days != null && days > 3 ? 'color:var(--danger);' : 'color:var(--text-muted);'}">${days != null ? days + 'd' : ''}</span>
        <button class="btn btn-secondary btn-sm rv-queue-respond" data-review-id="${r.hostaway_review_id}" style="font-size:10px; padding:2px 9px;">Mark responded</button>
      </div>`;
    }).join('');
    el.innerHTML = `
      <div class="pf-section" style="margin-bottom:20px; padding:14px 18px;">
        <div style="display:flex; align-items:baseline; gap:10px; margin-bottom:4px;">
          <h3 style="margin:0; font-size:14px;">Needs response</h3>
          <span style="font-size:11px; color:var(--text-muted);">${items.length} low-rated review${items.length === 1 ? '' : 's'} without a reply — oldest first</span>
          ${items.length > 10 ? `<a id="rv-queue-all" style="margin-left:auto; font-size:12px; color:var(--info); cursor:pointer;">view all ${items.length} →</a>` : ''}
        </div>
        ${rows}
      </div>`;
    el.querySelectorAll<HTMLElement>('.rv-queue-respond').forEach(btn =>
      btn.addEventListener('click', () => this.markResponded(parseInt(btn.dataset.reviewId || '0', 10))));
    document.getElementById('rv-queue-all')?.addEventListener('click', () => {
      this.filterResponse = 'needs-response';
      this.filterStatus = 'all';
      this.page = 1;
      this.renderList();
      document.getElementById('rv-list-container')?.scrollIntoView({ behavior: 'smooth' });
    });
  }

  private renderFilterBar(): void {
    const el = document.getElementById('rv-filter-bar');
    if (!el) return;

    // Build unique listing names
    const uniqueNames = Array.from(new Set(this.allRows.map(r => r.listing_name).filter(Boolean))).sort();

    el.innerHTML = `
      <div style="display:flex; align-items:center; gap:8px; flex-wrap:wrap;">

        <!-- Search -->
        <div style="position:relative;">
          <span style="position:absolute; left:10px; top:50%; transform:translateY(-50%); font-size:12px; color:var(--text-muted); pointer-events:none; line-height:1;">🔍</span>
          <input type="search" id="rv-search" class="rv-filter-select" placeholder="Search reviewer, property, text…" value="${Formatters.escapeHtml(this.search)}"
            style="padding:7px 10px 7px 30px; border:1px solid var(--border); border-radius:8px; font-size:12px; background:#fff; min-width:200px; color:var(--text);">
        </div>

        <!-- Property dropdown -->
        <select id="rv-listing-select" class="rv-filter-select" style="padding:7px 10px; border:1px solid var(--border); border-radius:8px; font-size:12px; background:#fff; cursor:pointer; min-width:160px;">
          <option value="">All Properties</option>
          ${uniqueNames.map(n => `<option value="${Formatters.escapeHtml(n)}" ${n === this.filterListingId ? 'selected' : ''}>${Formatters.escapeHtml(n)}</option>`).join('')}
        </select>

        <!-- Status tabs -->
        <div class="rv-tab-group" style="display:inline-flex; background:var(--border-soft); border-radius:8px; padding:3px; gap:2px;">
          <button class="rv-tab ${this.filterStatus === 'all' ? 'rv-tab-active' : ''}" data-status="all">All</button>
          <button class="rv-tab ${this.filterStatus === 'published' ? 'rv-tab-active' : ''}" data-status="published">Published</button>
          <button class="rv-tab ${this.filterStatus === 'pending' ? 'rv-tab-active' : ''}" data-status="pending">Pending</button>
          <button class="rv-tab ${this.filterChallenge ? 'rv-tab-active' : ''}" data-challenge="1" title="Reviews we challenged with the platform — and how it went">⚑ Challenged</button>
        </div>

        <!-- Rating chips -->
        <div style="display:flex; gap:6px; align-items:center;">
          <span style="font-size:11px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.05em;">Rating:</span>
          ${this.ratingChip('all', 'All')}
          ${this.ratingChip('5', '5★')}
          ${this.ratingChip('4', '4★')}
          ${this.ratingChip('low', '<4★')}
        </div>

      </div>
    `;

    // Bind events
    const searchEl = el.querySelector<HTMLInputElement>('#rv-search');
    searchEl?.addEventListener('input', () => {
      this.search = searchEl.value;
      this.page = 1;
      this.renderList();
      // Refocus after re-render (list re-renders only; bar is untouched, but be safe).
      const next = document.getElementById('rv-search') as HTMLInputElement | null;
      if (next) { next.focus(); next.setSelectionRange(next.value.length, next.value.length); }
    });

    const select = el.querySelector<HTMLSelectElement>('#rv-listing-select');
    select?.addEventListener('change', () => {
      this.filterListingId = select.value;
      this.page = 1;
      this.renderList();
    });

    el.querySelectorAll<HTMLButtonElement>('.rv-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.dataset.challenge) {
          this.filterChallenge = !this.filterChallenge;
          this.page = 1; this.renderFilterBar(); this.renderList(); return;
        }
        if (btn.dataset.response) {
          this.filterResponse = this.filterResponse === 'needs-response' ? 'all' : 'needs-response';
          this.filterStatus = 'all';
        } else {
          this.filterStatus = btn.dataset.status ?? 'all';
          this.filterResponse = 'all';
        }
        el.querySelectorAll('.rv-tab:not([data-challenge])').forEach(b => b.classList.remove('rv-tab-active'));   // R104 review: the ⚑ chip keeps its own state
        btn.classList.add('rv-tab-active');
        this.page = 1;
        this.renderList();
      });
    });

    el.querySelectorAll<HTMLButtonElement>('.rv-rating-chip').forEach(btn => {
      btn.addEventListener('click', () => {
        this.filterRating = btn.dataset.rating ?? 'all';
        el.querySelectorAll('.rv-rating-chip').forEach(b => b.classList.remove('rv-chip-active'));
        btn.classList.add('rv-chip-active');
        this.page = 1;
        this.renderList();
      });
    });
  }

  private ratingChip(value: string, label: string): string {
    const active = this.filterRating === value ? 'rv-chip-active' : '';
    return `<button class="pf-chip rv-rating-chip ${active}" data-rating="${value}" style="cursor:pointer; border:none;">${label}</button>`;
  }

  private isNeedsResponse(r: ReviewRow): boolean {
    const lowRating = r.rating !== null && r.rating < 4.0;
    const noReply = !r.reviewee_response;
    return lowRating && noReply && !r.marked_responded;
  }

  private needsResponseCount(): number {
    return this.allRows.filter(r => this.isNeedsResponse(r)).length;
  }

  // Server-side flag — the queue is shared, every teammate sees the same list.
  private markResponded(id: number): void {
    const row = this.allRows.find(r => r.hostaway_review_id === id);
    const repaint = () => { this.renderKpis(); this.renderQueue(); this.renderFilterBar(); this.renderList(); };
    // R114 fix (data-safety #5): a refused / failed save used to vanish silently
    // (the row looked marked until the next load) — roll back and say so.
    fetch(`/api/reviews/${id}/responded`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' })
      .then(async r => { if (!r.ok) { const j = await r.json().catch(() => null); throw new Error((j && j.error) || `HTTP ${r.status}`); } })
      .catch((e: any) => {
        if (row) row.marked_responded = false;
        repaint();
        alert(`Couldn't mark the review responded — ${e && e.message ? e.message : 'try again.'}`);
      });
    if (row) row.marked_responded = true;
    repaint();
  }

  private filteredRows(): ReviewRow[] {
    const q = this.search.trim().toLowerCase();
    return this.allRows.filter(r => {
      if (q) {
        const hay = `${r.reviewer_name || ''} ${r.listing_name || ''} ${r.public_review || ''} ${r.private_feedback || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (this.filterListingId && r.listing_name !== this.filterListingId) return false;
      if (this.filterChallenge && !(r.challenge && r.challenge.status)) return false;
      if (this.filterResponse === 'needs-response') return this.isNeedsResponse(r);
      if (this.filterStatus !== 'all' && r.status?.toLowerCase() !== this.filterStatus) return false;
      if (this.filterRating === '5' && (r.rating == null || r.rating < 4.5)) return false;
      if (this.filterRating === '4' && (r.rating == null || r.rating < 3.5 || r.rating >= 4.5)) return false;
      if (this.filterRating === 'low' && (r.rating === null || r.rating >= 3.5)) return false;
      return true;
    });
  }

  private renderList(): void {
    const el = document.getElementById('rv-list-container');
    if (!el) return;

    const rows = this.filteredRows();

    if (!rows.length) {
      el.innerHTML = emptyState(
        'No reviews match',
        'No reviews match the current search, property, status, rating, or response filters. Try widening the filters above.',
        '⭐',
      );
      return;
    }

    const pg = paginate(rows, this.page, this.pageSize);
    this.page = pg.page; // adopt the clamped page

    el.innerHTML = `
      <div class="rv-review-list">
        ${pg.items.map(r => this.renderReviewRow(r)).join('')}
      </div>
      ${pager({ idPrefix: 'rv-pager', page: pg.page, pages: pg.pages, total: pg.total, pageSize: this.pageSize })}
    `;

    document.getElementById('rv-pager-prev')?.addEventListener('click', () => {
      this.page--;
      this.renderList();
    });
    document.getElementById('rv-pager-next')?.addEventListener('click', () => {
      this.page++;
      this.renderList();
    });

    el.querySelectorAll<HTMLButtonElement>('.rv-mark-responded-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = parseInt(btn.dataset.reviewId ?? '0');
        if (id) this.markResponded(id);
      });
    });

    // R104 #4: delegated — survives the in-place item replacement toggleExpand does.
    if (!(el as any)._rvDelegated) {
      (el as any)._rvDelegated = true;
      el.addEventListener('click', (e) => {
        const t = e.target as HTMLElement;
        const ch = t.closest('[data-rv-challenge]') as HTMLElement | null;
        if (ch) { e.stopPropagation(); const [id, status] = (ch.dataset.rvChallenge || '').split('|'); void this.setChallenge(parseInt(id, 10), status); return; }
        const pr = t.closest('[data-rv-prop]') as HTMLElement | null;
        if (pr) { e.stopPropagation(); this.showPropertyReviews(pr.dataset.rvProp || ''); }
      });
    }
    el.querySelectorAll<HTMLElement>('.rv-review-item').forEach(item => {
      const id = parseInt(item.dataset.reviewId ?? '0');
      item.addEventListener('click', (e) => {
        // Don't re-collapse if they click the expand button itself — just toggle
        const target = e.target as HTMLElement;
        if (target.closest('.rv-expand-btn') || target.closest('.rv-mark-responded-btn') || target.closest('[data-rv-challenge]') || target.closest('[data-rv-prop]')) return;
        this.toggleExpand(id, item);
      });

      const expandBtn = item.querySelector<HTMLElement>('.rv-expand-btn');
      expandBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleExpand(id, item);
      });
    });
  }

  private toggleExpand(id: number, item: HTMLElement): void {
    if (this.expandedIds.has(id)) {
      this.expandedIds.delete(id);
    } else {
      this.expandedIds.add(id);
    }

    const row = this.allRows.find(r => r.hostaway_review_id === id);
    if (!row) return;

    // Re-render just this item in place
    const newItem = document.createElement('div');
    newItem.innerHTML = this.renderReviewRow(row);
    const newEl = newItem.firstElementChild as HTMLElement;
    if (newEl) {
      item.replaceWith(newEl);
      newEl.querySelector<HTMLElement>('.rv-expand-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleExpand(id, newEl);
      });
      newEl.querySelector<HTMLButtonElement>('.rv-mark-responded-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.markResponded(id);
      });
      newEl.addEventListener('click', (e) => {
        const t = e.target as HTMLElement;
        // R104 review: the challenge buttons / property link are delegated on the
        // container — this item handler must not collapse the row first
        if (t.closest('.rv-mark-responded-btn') || t.closest('[data-rv-challenge]') || t.closest('[data-rv-prop]')) return;
        this.toggleExpand(id, newEl);
      });
    }
  }

  // R104 #4 — challenge status: ⚑ Challenge → Successful / Denied (shown as a pill), withdraw resets.
  private challengePill(r: ReviewRow): string {
    const c = r.challenge; if (!c || !c.status) return '';
    const map: Record<string, [string, string]> = { challenged: ['pill-amber', '⚑ Challenged'], successful: ['pill-green', '⚑ Challenge successful'], denied: ['pill-red', '⚑ Challenge denied'] };
    const [cls, label] = map[c.status] || ['pill-gray', c.status];
    return `<span class="pill ${cls}" title="${Formatters.escapeHtml(`${c.by ? `${c.by} · ` : ''}${c.at ? Formatters.formatDate(String(c.at).slice(0, 10)) : ''}${c.note ? ` — ${c.note}` : ''}`)}">${label}</span>`;
  }
  private challengeBar(r: ReviewRow): string {
    const st = (r.challenge && r.challenge.status) || '';
    const id = r.hostaway_review_id;
    const b = (status: string, label: string, cls = 'btn-secondary') => `<button class="btn ${cls} btn-sm" data-rv-challenge="${id}|${status}" style="font-size:10.5px; padding:2px 9px;">${label}</button>`;
    return `<div style="display:flex; gap:6px; align-items:center; flex-wrap:wrap; margin:6px 0 2px; font-size:11.5px; color:var(--text-muted);">
      <span style="font-weight:600;">Challenge:</span>
      ${!st ? b('challenged', '⚑ Mark as challenged') : ''}
      ${st === 'challenged' ? `${b('successful', '✓ Successful', 'btn-success')} ${b('denied', '✕ Denied')} ${b('none', 'Withdraw')}` : ''}
      ${st === 'successful' || st === 'denied' ? `${b('challenged', 'Re-open challenge')} ${b('none', 'Clear')}` : ''}
      ${r.challenge && r.challenge.note ? `<span>“${Formatters.escapeHtml(r.challenge.note)}”</span>` : ''}
    </div>`;
  }
  private async setChallenge(id: number, status: string): Promise<void> {
    if (!id) return;
    let note = '';
    if (status === 'challenged') { const n = window.prompt('Why are we challenging this review? (optional note for the team)', ''); if (n === null) return; note = n.trim(); }
    try {
      const r = await fetch(`/api/reviews/${id}/challenge`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status, note }) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      const row = this.allRows.find(x => x.hostaway_review_id === id);
      if (row) row.challenge = j.challenge || null;
      const item = document.querySelector(`.rv-review-item[data-review-id="${id}"]`) as HTMLElement | null;
      if (row && item) { const wrap = document.createElement('div'); wrap.innerHTML = this.renderReviewRow(row); const nextEl = wrap.firstElementChild as HTMLElement; if (nextEl) { item.replaceWith(nextEl); nextEl.querySelector<HTMLElement>('.rv-expand-btn')?.addEventListener('click', (e) => { e.stopPropagation(); this.toggleExpand(id, nextEl); }); nextEl.querySelector<HTMLButtonElement>('.rv-mark-responded-btn')?.addEventListener('click', (e) => { e.stopPropagation(); this.markResponded(id); });   /* review R104 #26 */ nextEl.addEventListener('click', (e) => { const t = e.target as HTMLElement; if (t.closest('.rv-expand-btn') || t.closest('.rv-mark-responded-btn') || t.closest('[data-rv-challenge]') || t.closest('[data-rv-prop]')) return; this.toggleExpand(id, nextEl); }); } }
      else this.renderList();
    } catch (e: any) { alert(`Couldn't update the challenge status — ${e.message}`); }
  }
  // R104 #4: "a review pulls up all the reviews for a property" — from a review row or the Scores table.
  private showPropertyReviews(name: string): void {
    if (!name) return;
    this.filterListingId = name;
    this.search = ''; this.filterStatus = 'all'; this.filterRating = 'all'; this.filterResponse = 'all'; this.filterChallenge = false;
    this.page = 1;
    this.renderFilterBar();
    this.renderList();
    document.getElementById('rv-list-container')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private renderReviewRow(r: ReviewRow): string {
    const expanded = this.expandedIds.has(r.hostaway_review_id);
    const initials = this.getInitials(r.reviewer_name);
    const stars = this.renderStarsHtml(r.rating);
    const shortName = r.listing_name || 'Unknown';
    const truncated = r.public_review && r.public_review.length > 120
      ? r.public_review.slice(0, 120).trim() + '…'
      : r.public_review || '';
    const fullReview = r.public_review || '';
    const departureLabel = r.departure_date ? Formatters.formatDate(r.departure_date) : '—';
    const submittedLabel = r.submitted_at ? Formatters.formatDate(r.submitted_at) : '—';
    const hasResponse = !!r.reviewee_response;
    const markedResponded = !!r.marked_responded;
    const needsResp = this.isNeedsResponse(r);
    const statusChip = this.statusChip(r.status);
    const recommendedBadge = r.is_recommended === true
      ? '<span class="pill pill-green">Recommended</span>'
      : r.is_recommended === false
        ? '<span class="pill pill-red">Not recommended</span>'
        : '';

    const rowBg = expanded ? '#f8f7ff' : needsResp ? '#fff7f7' : '#fff';

    return `
      <div class="rv-review-item ${expanded ? 'rv-expanded' : ''}" data-review-id="${r.hostaway_review_id}" style="border-bottom:1px solid var(--border); padding:16px 20px; cursor:pointer; transition:background 0.12s; background:${rowBg};">

        <!-- Row top -->
        <div style="display:flex; align-items:flex-start; gap:14px;">

          <!-- Avatar -->
          <div class="rv-avatar">${initials}</div>

          <!-- Content -->
          <div style="flex:1; min-width:0;">
            <div style="display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:4px;">
              <span style="font-size:13px; font-weight:600;">${Formatters.escapeHtml(r.reviewer_name || 'Guest')}</span>
              <button data-rv-prop="${Formatters.escapeHtml(shortName)}" title="Show every review for this property" style="background:none; border:none; padding:0; font-size:11px; color:var(--accent,#2563eb); cursor:pointer; font-family:inherit;">${Formatters.escapeHtml(shortName)}</button>
              <span style="margin-left:auto; display:flex; align-items:center; gap:6px; flex-wrap:wrap;">
                ${statusChip}
                ${recommendedBadge}
                ${r.private_feedback ? '<span class="pill pill-blue" title="This guest also left private feedback — expand to read it">Private feedback</span>' : ''}
                ${needsResp ? '<span class="pill pill-red">Needs response</span>' : ''}
                ${hasResponse || markedResponded ? '<span class="pill pill-gray">Responded</span>' : ''}
                ${this.challengePill(r)}
              </span>
            </div>
            ${expanded ? this.challengeBar(r) : ''}

            <!-- Stars + dates -->
            <div style="display:flex; align-items:center; gap:12px; margin-bottom:8px; flex-wrap:wrap;">
              <span class="rv-stars">${stars}</span>
              <span style="font-size:11px; color:var(--text-muted);">Departed ${departureLabel}</span>
              <span style="font-size:11px; color:var(--text-muted);">Submitted ${submittedLabel}</span>
            </div>

            <!-- Review text — the .expanded class lifts the 2-line CSS clamp so
                 the FULL public review shows; pre-wrap keeps guest paragraphs. -->
            <div class="rv-review-text${expanded ? ' expanded' : ''}" style="font-size:13px; color:${expanded ? 'var(--text)' : 'var(--text-muted)'}; line-height:1.5;${expanded ? ' white-space:pre-wrap; overflow-wrap:anywhere;' : ''}">
              ${expanded
                ? (fullReview ? Formatters.escapeHtml(fullReview) : '<em style="color:#cbd5e1;">No public review text</em>')
                : (r.public_review ? Formatters.escapeHtml(truncated) : '<em style="color:#cbd5e1;">No public review text</em>')}
            </div>

            ${expanded && r.private_feedback ? `
            <div class="rv-private-block" style="margin-top:12px; padding:12px 14px; background:var(--info-soft); border:1px solid var(--border); border-radius:8px;">
              <div style="font-size:10px; font-weight:700; color:var(--info-text); text-transform:uppercase; letter-spacing:0.06em; margin-bottom:6px;">Private feedback (guest → host only)</div>
              <div style="font-size:13px; color:var(--text-muted); line-height:1.5; white-space:pre-wrap; overflow-wrap:anywhere;">${Formatters.escapeHtml(r.private_feedback)}</div>
            </div>` : ''}

            ${expanded && hasResponse ? `
            <div class="rv-response-block" style="margin-top:12px; padding:12px 14px; background:var(--success-soft); border-left:3px solid var(--success); border-radius:0 8px 8px 0;">
              <div style="font-size:10px; font-weight:700; color:var(--success-text); text-transform:uppercase; letter-spacing:0.06em; margin-bottom:6px;">Host Response</div>
              <div style="font-size:13px; color:var(--text); line-height:1.5;">${Formatters.escapeHtml(r.reviewee_response!)}</div>
            </div>` : ''}

            ${expanded && needsResp ? `
            <div style="margin-top:12px; display:flex; align-items:center; gap:10px;">
              <button class="rv-mark-responded-btn btn btn-success btn-sm" data-review-id="${r.hostaway_review_id}">✓ Mark as Responded</button>
              <span style="font-size:12px; color:var(--text-muted);">Have you replied on Airbnb/VRBO?</span>
            </div>` : ''}

          </div>

          <!-- Expand chevron -->
          <button class="rv-expand-btn" style="background:none; border:none; cursor:pointer; color:#cbd5e1; font-size:18px; padding:0 4px; flex-shrink:0; transition:color 0.12s; line-height:1;">
            ${expanded ? '▲' : '▼'}
          </button>

        </div>
      </div>
    `;
  }

  private getInitials(name: string | null): string {
    if (!name) return '?';
    return name.split(' ').filter(Boolean).map(w => w[0].toUpperCase()).slice(0, 2).join('');
  }

  private renderStarsHtml(rating: number | null): string {
    if (rating === null) return '<span style="color:#cbd5e1; font-size:12px;">No rating</span>';
    const full = Math.round(rating);
    return Array.from({ length: 5 }, (_, i) =>
      `<span style="color:${i < full ? '#f59e0b' : '#e2e8f0'}; font-size:14px;">★</span>`
    ).join('');
  }

  private renderStarsFill(avg: number): string {
    if (!avg || !Number.isFinite(Number(avg))) return '';
    const full = Math.floor(avg);
    const half = avg - full >= 0.5;
    return Array.from({ length: 5 }, (_, i) => {
      if (i < full) return '★';
      if (i === full && half) return '½';
      return '☆';
    }).join('');
  }

  private statusChip(status: string | null): string {
    if (!status) return '';
    const s = status.toLowerCase();
    const cls = s === 'published' ? 'pill-green'
      : s === 'pending' ? 'pill-amber'
      : 'pill-gray';
    return `<span class="pill ${cls}" style="text-transform:capitalize;">${Formatters.escapeHtml(status)}</span>`;
  }
}
